import React from "react";
import {connect} from "react-redux";
import {LoadingBoton} from "../../../_helpers";
import {Form, Formik} from "formik";
import * as Yup from 'yup';
import alertService from "../../../_services/cart/alert.service";
import http from "../../../_services/cart/http.service";
import Select from "react-select";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

import CargadorVersiones from "../../../_helpers/CargadorVersiones";
/**
 * Error de un combo del modal.
 *
 * <p>No se usa el `ErrorMessage` de `_helpers` porque ese muestra el error recien cuando el
 * campo esta `touched`, y react-select no dispara el onBlur de Formik: los campos nunca se
 * marcaban como tocados y el error no aparecia nunca. Aca alcanza con haber intentado enviar.
 */
const ErrorCampo = ({intentado, error}) =>
    intentado && error ? (
        <div className="help-block text-danger">
            <i className="fas fa-exclamation-circle"></i> {error}
        </div>
    ) : null;

/**
 * Alta de una alerta "avisame cuando entre este repuesto".
 *
 * <p>El backend (AlertaProductoServiceImpl#crearAlerta) identifica la alerta por
 * marca + modelo + version + tipo de repuesto, y trata el tipo nulo como comodin: sin tipo
 * la alerta matchea con cualquier repuesto de ese auto. De ahi que los tres primeros sean
 * obligatorios y el repuesto no.
 */
class AlertaModal extends React.Component {

    state = {
        grabando: false
    };

    buscarMisModelos = (modelos, marcaId) => {
        if (marcaId === undefined) {
            return [];
        }

        return modelos?.filter((it) => it.marca.id === marcaId) ?? [];
    };

    buscarMisVersiones = (versiones, modeloId) => {
        if (modeloId === undefined) {
            return [];
        }

        return versiones?.filter((it) => it.modelo.id === modeloId) ?? [];
    };

    /**
     * Los combos dependen en cadena, asi que al cambiar uno hay que limpiar los de abajo: si
     * no, queda seleccionado un modelo que ya no pertenece a la marca elegida y la alerta se
     * graba con una combinacion que no existe.
     */
    elegirMarca = (setFieldValue, opt) => {
        setFieldValue("marca", opt ?? undefined);
        setFieldValue("modelo", undefined);
        setFieldValue("version", undefined);
    };

    elegirModelo = (setFieldValue, opt) => {
        setFieldValue("modelo", opt ?? undefined);
        setFieldValue("version", undefined);
    };

    render() {
        const {producto, filtros, handleClose} = this.props;

        const inic = {
            marca: producto?.marca,
            modelo: producto?.modelo,
            version: producto?.version,
            tipoRepuesto: producto?.tipoRepuesto,
        };

        const validationSchema = Yup.object().shape({
            marca: Yup.object({id: Yup.string().required("Elegí una marca")}),
            modelo: Yup.object({id: Yup.string().required("Elegí un modelo")}),
            version: Yup.object({id: Yup.string().required("Elegí una versión")}),
        });

        // Los combos del modal son mas bajos que los de la barra lateral: aca hay cuatro
        // seguidos y con 58px de alto cada uno el formulario no entraba en pantalla.
        const customSelect = {
            control: (provided) => ({
                ...provided,
                background: "transparent",
                borderColor: "transparent",
                minHeight: "42px",
                height: "42px",
            }),
            menuPortal: (provided) => ({...provided, zIndex: 2000}),
        };

        const opcionesSelect = {
            className: "sideBar-select",
            isClearable: true,
            getOptionLabel: (option) => `${option.nombre}`,
            getOptionValue: (option) => option.id,
            components: {IndicatorSeparator: () => null},
            styles: customSelect,
            // El menu va al body: adentro del modal lo recortaba el scroll del .modal-body.
            menuPortalTarget: typeof document !== "undefined" ? document.body : undefined,
        };

        return (
            <Formik
                enableReinitialize={true}
                initialValues={inic}
                validationSchema={validationSchema}
                onSubmit={(values) => {
                    this.setState({grabando: true});
                    http.post('/public/producto/crearAlerta', values).then(
                        () => {
                            alertService.msg('Listo. Te avisamos apenas ingrese un repuesto con estas características.');
                            this.setState({grabando: false});
                            handleClose();
                        },
                        error => {
                            alertService.error(error);
                            this.setState({grabando: false});
                        }
                    );
                }}>
                {({values, setFieldValue, errors, submitCount}) => (
                    <Form className="alerta">
                        <p className="alerta__intro">
                            <FontAwesomeIcon icon="fa-solid fa-circle-info"/>
                            <span>
                                Decinos qué auto te interesa y te avisamos por mail y notificación
                                cuando un vendedor publique un repuesto que encaje.
                            </span>
                        </p>

                        <div className="alerta__campos">
                            <div className="alerta__campo">
                                <label>Marca</label>
                                <Select
                                    {...opcionesSelect}
                                    placeholder="Elegí una marca"
                                    value={filtros?.marcas?.find((o) => o.id === values.marca?.id) ?? null}
                                    options={filtros?.marcas}
                                    noOptionsMessage={() => "No se encontraron marcas"}
                                    onChange={(opt) => this.elegirMarca(setFieldValue, opt)}
                                />
                                <ErrorCampo intentado={submitCount > 0} error={errors.marca?.id}/>
                            </div>

                            <div className="alerta__campo">
                                <label>Modelo</label>
                                <Select
                                    {...opcionesSelect}
                                    placeholder={values.marca?.id ? "Elegí un modelo" : "Elegí primero la marca"}
                                    isDisabled={!values.marca?.id}
                                    value={filtros?.modelos?.find((o) => o.id === values.modelo?.id) ?? null}
                                    options={this.buscarMisModelos(filtros?.modelos, values.marca?.id)}
                                    noOptionsMessage={() => "No se encontraron modelos"}
                                    onChange={(opt) => this.elegirModelo(setFieldValue, opt)}
                                />
                                <ErrorCampo intentado={submitCount > 0} error={errors.modelo?.id}/>
                            </div>

                            <CargadorVersiones modeloId={values.modelo?.id}/>

                            <div className="alerta__campo">
                                <label>Versión</label>
                                <Select
                                    {...opcionesSelect}
                                    placeholder={values.modelo?.id ? "Elegí una versión" : "Elegí primero el modelo"}
                                    isDisabled={!values.modelo?.id}
                                    value={filtros?.versiones?.find((o) => o.id === values.version?.id) ?? null}
                                    options={this.buscarMisVersiones(filtros?.versiones, values.modelo?.id)}
                                    noOptionsMessage={() => "No se encontraron versiones"}
                                    onChange={(opt) => setFieldValue("version", opt ?? undefined)}
                                />
                                <ErrorCampo intentado={submitCount > 0} error={errors.version?.id}/>
                            </div>

                            <div className="alerta__campo">
                                <label>
                                    Repuesto <span className="alerta__opcional">opcional</span>
                                </label>
                                <Select
                                    {...opcionesSelect}
                                    placeholder="Cualquier repuesto"
                                    value={filtros?.tipoRepuestos?.find((o) => o.id === values.tipoRepuesto?.id) ?? null}
                                    options={filtros?.tipoRepuestos}
                                    noOptionsMessage={() => "No se encontraron repuestos"}
                                    onChange={(opt) => setFieldValue("tipoRepuesto", opt ?? undefined)}
                                />
                                <small className="alerta__ayuda">
                                    Si lo dejás vacío te avisamos por cualquier repuesto de ese auto.
                                </small>
                            </div>
                        </div>

                        <div className="alerta__acciones">
                            <button
                                className="button"
                                data-type="complementary-small"
                                type="button"
                                onClick={handleClose}
                            >
                                Cancelar
                            </button>
                            <button
                                className="button"
                                data-type="primary-small"
                                type="submit"
                                disabled={this.state.grabando}
                            >
                                <FontAwesomeIcon icon="fa-solid fa-bell"/>
                                <span>Crear alerta</span>
                                <LoadingBoton grabando={this.state.grabando}/>
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
        );
    }
}

function mapState(state) {
    const {filtros} = state.busqueda;
    return {filtros};
}

const connectedAlertaModal = connect(mapState, {})(AlertaModal);
export {connectedAlertaModal as AlertaModal};
